<script setup>
import {Dialog, DialogOverlay, DialogPanel, TransitionChild, TransitionRoot} from "@headlessui/vue";
import {Icon} from "@iconify/vue";
import {computed, reactive, ref} from "vue";
import {required, numeric} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import showAlert from "@/utils/showAlert.js";
import {showMessage} from "@/utils/showMessage.js";
import {useManagementStore} from "@/stores/management.js";

const managementStore = useManagementStore();
const emit = defineEmits(['closeModal', 'addedNewStore']);
const isLoading = ref(false);
const saving = ref(false);

/* Auxiliary keys allowed for all fields */
const helperKeys = [
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'Home', 'End'
]

function closeModal() {
  emit('closeModal');
}

const store = reactive({
  "store": "",
  "origin": "",
  "currency": "",
  "commissionFee": "",
  "withdrawalFee": "",
  "account": "",
})

const rules = computed(() => {
  return {
    store: {required},
    origin: {required},
    account: {required},
    currency: {required},
    commissionFee: {required, numeric},
    withdrawalFee: {required, numeric},
  };
});
const $vulidate = useVuelidate(rules, store);

async function onSave() {
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    return false;
  }

  //const ok = await showAlert('You are adding new store?', 'Are you sure?', 'Yes', 'No')
  //if (!ok) return

  // API call
  try {
    saving.value = true
    const res = await managementStore.action_addStore({
      ...store,
      commissionFee: (Number((store.commissionFee / 100).toFixed(4))).toString(),
      withdrawalFee: (Number((store.withdrawalFee / 100).toFixed(4))).toString(),
    })

    if (res === true) {
      showMessage('New Store added!', 'success')
      emit('addedNewStore')
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    saving.value = false
  }
}

/* Digit only (no dots) */
function allowDigits(e) {
  if (
      helperKeys.includes(e.key) ||
      // Allow Ctrl+C / Ctrl+V / Ctrl+A and…:
      (e.ctrlKey || e.metaKey)
  ) return

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

/* Digit + a single dot  */
function allowDigitsDot(e) {
  if (
      helperKeys.includes(e.key) ||
      (e.ctrlKey || e.metaKey)
  ) return

  if (e.key === '.') {
    if (e.target.value.includes('.')) e.preventDefault()
    return
  }

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}
</script>

<template>
  <TransitionRoot appear show as="template">
    <Dialog as="div" @close="closeModal" class="relative z-[999]">
      <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-[black]/60"/>
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex h-full overflow-hidden items-center justify-center px-4 py-8">
          <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
          >
            <DialogPanel
                class="w-full flex flex-col overflow-hidden rounded-md max-w-sm h-auto text-left align-middle shadow-xl transition-all bg-white dark:bg-[#0e1726] text-black-600 dark:text-white">
              <div class="h-full flex flex-col">
                <div class="flex flex-row gap-10 justify-between px-4 border-b border-slate-700">
                  <div class="text-base py-3">
                    Add Store
                  </div>
                  <div class="flex flex-row items-center gap-4">
                    <div class="h-full w-[1px] bg-slate-700"></div>
                    <button
                        type="button"
                        class="text-white-dark outline-none hover:text-red-500"
                        @click="closeModal"
                    >
                      <Icon icon="pajamas:close-xs"/>
                    </button>
                  </div>
                </div>
                <div class="p-4 flex-1 overflow-auto h-full flex flex-col gap-6">
                  <p v-if="isLoading">Loading...</p>
                  <div v-else class="space-y-5">
                    <div class="flex flex-col gap-0.5 text-xs">
                      <label for="store">Store:</label>
                      <div class="">
                        <input id="store"
                               type="text"
                               v-model="store.store"
                               :class="{ '!border !border-red-300': $vulidate.store.$error }"
                               class="input-control w-full block focus:outline-none h-[32px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                      </div>
                      <template v-if="$vulidate.store.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.store.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                    <div class="flex flex-col gap-0.5 text-xs">
                      <label for="origin">Origin:</label>
                      <div class="">
                        <input id="origin"
                               type="text"
                               v-model="store.origin"
                               :class="{ '!border !border-red-300': $vulidate.origin.$error }"
                               class="input-control w-full block focus:outline-none h-[32px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                      </div>
                      <template v-if="$vulidate.origin.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.origin.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                    <div class="flex flex-col gap-0.5 text-xs">
                      <label for="account">Account:</label>
                      <div class="">
                        <input id="account"
                               type="text"
                               v-model="store.account"
                               :class="{ '!border !border-red-300': $vulidate.account.$error }"
                               class="input-control w-full block focus:outline-none h-[32px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                      </div>
                      <template v-if="$vulidate.account.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.account.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                    <div class="flex flex-col gap-0.5 text-xs">
                      <label for="currency">Currency:</label>
                      <div class="">
                        <input id="currency"
                               type="text"
                               v-model="store.currency"
                               :class="{ '!border !border-red-300': $vulidate.currency.$error }"
                               class="input-control w-full block focus:outline-none h-[32px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                      </div>
                      <template v-if="$vulidate.currency.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.currency.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                    <div class="flex flex-col gap-0.5 text-xs">
                      <label for="commissionFee">Commission Fee:</label>
                      <div class="">
                        <input id="commissionFee"
                               type="text"
                               v-model="store.commissionFee"
                               @keydown="allowDigitsDot"
                               :class="{ '!border !border-red-300': $vulidate.commissionFee.$error }"
                               class="input-control w-full block focus:outline-none h-[32px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                      </div>
                      <template v-if="$vulidate.commissionFee.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.commissionFee.required.$response">
                          This field is required
                        </p>
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.commissionFee.numeric.$response">
                          This field should be a number
                        </p>
                      </template>
                    </div>
                    <div class="flex flex-col gap-0.5 text-xs">
                      <label for="withdrawalFee">Withdrawal Fee:</label>
                      <div class="">
                        <input id="withdrawalFee"
                               type="text"
                               v-model="store.withdrawalFee"
                               @keydown="allowDigitsDot"
                               :class="{ '!border !border-red-300': $vulidate.withdrawalFee.$error }"
                               class="input-control w-full block focus:outline-none h-[32px] rounded-md read-only:!bg-slate-200 read-only:dark:!text-black-300 read-only:!text-black-900 read-only:dark:!bg-slate-800">
                      </div>
                      <template v-if="$vulidate.withdrawalFee.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.withdrawalFee.required.$response">
                          This field is required
                        </p>
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.withdrawalFee.numeric.$response">
                          This field should be a number
                        </p>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="p-3 flex justify-end gap-3">
                  <button @click="onSave" class="btn btn-outline-primary py-1">
                    <span v-if="saving"
                          class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0"></span>
                    Save
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped lang="scss">

</style>